import { useLocales } from "@/locales";
import { CommonWrap } from "@/PageComponents/common";
import ContentWrap from "@/DocLayout/Content/Index";
import { Box, Link } from "@mui/material";
import CodeTable, {
  CodeRowProps,
  CodeTableLabelItemProps,
} from "@/DocLayout/CodeTable/CodeTable";
import { Trans } from "react-i18next";
import { TELEGRAM_SUPPORT_URL, VIEW_COLLECT_FEE_URL } from "@/config";

export default function ErrorCode() {
  const { translate, currentLang } = useLocales();

  return (
    <ContentWrap
      showBorderBottom={false}
      leftChildrenSx={{
        paddingTop: "1px",
        paddingBottom: "32px",
      }}
      leftChildren={
        <CommonWrap>
          <Box id="error-code" component={"div"} className="title_0">
            {translate("support_help.error_code_title")}
          </Box>
          {currentLang.value !== "en" && (
            <Box component={"div"} className="desc">
              {translate("support_help.error_code_desc")}
            </Box>
          )}

          <CommonCodeTable />
          <Box mb={3} />
          <MerchantCodeTable />
          <Box mb={3} />
          <CoinCodeTable />
          <Box mb={3} />
          <WithdrawCodeTable />
          <Box mb={3} />
          <DepositCodeTable />
        </CommonWrap>
      }
    />
  );
}

const CommonCodeTable = () => {
  const { translate, currentLang } = useLocales();

  const tableLabels: CodeTableLabelItemProps[] = [
    {
      id: "code",
      label: translate("common.error_code"),
    },
    {
      id: "tip",
      label: translate("common.error_tip"),
    },
    {
      id: "desc",
      label: translate("common.error_desc"),
    },
  ];

  const tableData: CodeRowProps[] = [
    {
      code: "11000",
      tip: "InvalidArgument",
      desc: translate("support_help.error_code_table1_desc0"),
    },
    {
      code: "11001",
      tip: "HeaderInvalidArgument",
      desc: translate("support_help.error_code_table1_desc1"),
    },
    {
      code: "11002",
      tip: "Internal",
      desc:
        currentLang.value === "en" ? (
          <Trans
            i18nKey={"support_help.error_code_table1_desc2"}
            t={translate as any}
          >
            s
            <Link
              href={TELEGRAM_SUPPORT_URL}
              target="_blank"
              className="link"
              underline="always"
            >
              Just a placeholder
            </Link>
          </Trans>
        ) : (
          translate("support_help.error_code_table1_desc2")
        ),
    },
    {
      code: "11003",
      tip: "NotFound",
      desc: translate("support_help.error_code_table1_desc3"),
    },
    {
      code: "11004",
      tip: "RateLimit",
      desc: translate("support_help.error_code_table1_desc4"),
    },
    {
      code: "11005",
      tip: "VerifySignFailed",
      desc: translate("support_help.error_code_table1_desc5"),
    },
    {
      code: "11006",
      tip: "ReqExpired",
      desc: translate("support_help.error_code_table1_desc6"),
    },
    {
      code: "11007",
      tip: "RepeatedSubmit",
      desc: translate("support_help.error_code_table1_desc7"),
    },
    {
      code: "11008",
      tip: "QueryDurationTooMax",
      desc: translate("support_help.error_code_table1_desc8"),
    },
    {
      code: "11009",
      tip: "ReqDailyLimit",
      desc: translate("support_help.error_code_table1_desc9"),
    },
    {
      code: "11010",
      tip: "QueryNumMax",
      desc: translate("support_help.error_code_table1_desc10"),
    },
    {
      code: "11011",
      tip: "OrderDuplicate",
      desc: translate("support_help.error_code_table1_desc11"),
    },
    {
      code: "11012",
      tip: "ExpiredAtTooMax",
      desc: translate("support_help.error_code_table1_desc12"),
    },
    {
      code: "11013",
      tip: "NoSupportVersion",
      desc: translate("support_help.error_code_table1_desc13"),
    },
    {
      code: "11014",
      tip: "MaliciousReq",
      desc: translate("support_help.error_code_table1_desc14"),
    },
    {
      code: "11015",
      tip: "UserIdNotFound",
      desc: translate("support_help.error_code_table1_desc15"),
    },
  ];

  return (
    <>
      <Box component={"div"} className="title_1">
        {translate("support_help.error_code_table1_title")}
      </Box>
      <CodeTable tableLabels={tableLabels} tableData={tableData} />
    </>
  );
};

const MerchantCodeTable = () => {
  const { translate } = useLocales();
  const tableLabels: CodeTableLabelItemProps[] = [
    {
      id: "code",
      label: translate("common.error_code"),
    },
    {
      id: "tip",
      label: translate("common.error_tip"),
    },
    {
      id: "desc",
      label: translate("common.error_desc"),
    },
  ];

  const tableData: CodeRowProps[] = [
    {
      code: "12000",
      tip: "MerchantDisabled",
      desc: translate("support_help.error_code_table2_desc0"),
    },
    {
      code: "12001",
      tip: "MerchantNotFound",
      desc: translate("support_help.error_code_table2_desc1"),
    },
    {
      code: "12002",
      tip: "IpNotInWhitelist",
      desc: translate("support_help.error_code_table2_desc2"),
    },
    {
      code: "12003",
      tip: "MerchantApiDisabled",
      desc: translate("support_help.error_code_table2_desc3"),
    },
  ];

  return (
    <>
      <Box component={"div"} className="title_1">
        {translate("support_help.error_code_table2_title")}
      </Box>
      <CodeTable tableLabels={tableLabels} tableData={tableData} />
    </>
  );
};

const CoinCodeTable = () => {
  const { translate } = useLocales();
  const tableLabels: CodeTableLabelItemProps[] = [
    {
      id: "code",
      label: translate("common.error_code"),
    },
    {
      id: "tip",
      label: translate("common.error_tip"),
    },
    {
      id: "desc",
      label: translate("common.error_desc"),
    },
  ];

  const tableData: CodeRowProps[] = [
    {
      code: "13000",
      tip: "InvalidCoin",
      desc: translate("support_help.error_code_table3_desc0"),
    },
    {
      code: "13001",
      tip: "InvalidChain",
      desc: translate("support_help.error_code_table3_desc1"),
    },
    {
      code: "13002",
      tip: "AbnormalCoinPrice",
      desc: translate("support_help.error_code_table3_desc2"),
    },
    {
      code: "13003",
      tip: "AbnormalCoinPriceNotSupportMode",
      desc: translate("support_help.error_code_table3_desc3"),
    },
    {
      code: "13004",
      tip: "UnstableBlockchain",
      desc: translate("support_help.error_code_table3_desc4"),
    },
  ];

  return (
    <>
      <Box component={"div"} className="title_1">
        {translate("support_help.error_code_table3_title")}
      </Box>
      <CodeTable tableLabels={tableLabels} tableData={tableData} />
    </>
  );
};

const WithdrawCodeTable = () => {
  const { translate, currentLang } = useLocales();
  const tableLabels: CodeTableLabelItemProps[] = [
    {
      id: "code",
      label: translate("common.error_code"),
    },
    {
      id: "tip",
      label: translate("common.error_tip"),
    },
    {
      id: "desc",
      label: translate("common.error_desc"),
    },
  ];

  const tableData: CodeRowProps[] = [
    {
      code: "14000",
      tip: "BalanceInsufficient",
      desc: translate("support_help.error_code_table4_desc0"),
    },
    {
      code: "14001",
      tip: "WithdrawFeeTooLow",
      desc: (
        <Trans
          i18nKey={"support_help.error_code_table4_desc1"}
          t={translate as any}
        >
          Just a placeholder
          <a href={"#get-withdrawal-network-fee"} className="link">
            Just a placeholder
          </a>
        </Trans>
      ),
    },
    {
      code: "14002",
      tip: "AddressNotActive",
      desc: translate("support_help.error_code_table4_desc2"),
    },
    {
      code: "14003",
      tip: "AddressEmptyMemo",
      desc: translate("support_help.error_code_table4_desc3"),
    },
    {
      code: "14004",
      tip: "ChainStopWithdraw",
      desc: translate("support_help.error_code_table4_desc4"),
    },
    {
      code: "14005",
      tip: "WithdrawValueLessThanLimit",
      desc:
        currentLang.value === "en" ? (
          <Trans
            i18nKey={"support_help.error_code_table4_desc5"}
            t={translate as any}
          >
            s
            <a href={"#get-token-list"} className="link">
              Just a placeholder
            </a>
          </Trans>
        ) : (
          translate("support_help.error_code_table4_desc5")
        ),
    },
    {
      code: "14006",
      tip: "WithdrawValueMoreThanLimit",
      desc: translate("support_help.error_code_table4_desc6"),
    },
    {
      code: "14007",
      tip: "WithdrawAddrFormat",
      desc: translate("support_help.error_code_table4_desc7"),
    },
    {
      code: "14008",
      tip: "WithdrawCannotSelf",
      desc: translate("support_help.error_code_table4_desc8"),
    },
    {
      code: "14009",
      tip: "CoinNoSupportMemo",
      desc: translate("support_help.error_code_table4_desc9"),
    },
    {
      code: "14010",
      tip: "NoSupportCoin",
      desc: translate("support_help.error_code_table4_desc10"),
    },
    {
      code: "14011",
      tip: "WithdrawFeeBalanceNotEnough",
      desc: translate("support_help.error_code_table4_desc11"),
    },
    {
      code: "14012",
      tip: "NotSupportMerchantTransfer",
      desc: translate("support_help.error_code_table4_desc12"),
    },
    {
      code: "14013",
      tip: "CoinPrecisionLimit",
      desc: translate("support_help.error_code_table4_desc13"),
    },
    {
      code: "14014",
      tip: "NotFinishCollection",
      desc: (
        <Trans
          i18nKey={"support_help.error_code_table4_desc14"}
          t={translate as any}
        >
          Just a placeholder
          <Link
            underline="always"
            target="_blank"
            href={VIEW_COLLECT_FEE_URL}
            className="link"
          >
            Just a placeholder
          </Link>
        </Trans>
      ),
    },
    {
      code: "14015",
      tip: "WithdrawToContractAddress",
      desc: translate("support_help.error_code_table4_desc15"),
    },
    {
      code: "14016",
      tip: "WithdrawInvalidAddressType",
      desc: translate("support_help.error_code_table4_desc16"),
    },
  ];

  return (
    <>
      <Box component={"div"} className="title_1">
        {translate("support_help.error_code_table4_title")}
      </Box>
      <CodeTable tableLabels={tableLabels} tableData={tableData} />
    </>
  );
};

const DepositCodeTable = () => {
  const { translate } = useLocales();
  const tableLabels: CodeTableLabelItemProps[] = [
    {
      id: "code",
      label: translate("common.error_code"),
    },
    {
      id: "tip",
      label: translate("common.error_tip"),
    },
    {
      id: "desc",
      label: translate("common.error_desc"),
    },
  ];

  const tableData: CodeRowProps[] = [
    {
      code: "15000",
      tip: "GenerateAddressFailed",
      desc: translate("support_help.error_code_table5_desc0"),
    },
    {
      code: "15001",
      tip: "PaymentAddressNumTooMuch",
      desc: translate("support_help.error_code_table5_desc1"),
    },

    {
      code: "15003",
      tip: "ChainStopDeposit",
      desc: translate("support_help.error_code_table5_desc3"),
    },
  ];

  return (
    <>
      <Box component={"div"} className="title_1">
        {translate("support_help.error_code_table5_title")}
      </Box>
      <CodeTable tableLabels={tableLabels} tableData={tableData} />
    </>
  );
};
